import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconLifeBuoy } from '~/ui/core/icons';

import { useNavigationMenu } from '../../app/app-bar/menu-bar-v2/NavigationMenuContext';
import { useSkillFeedbackModalContext } from './ModalContext';
import { useFeedbackDialogStore } from './use-feedback-dialog-store';

const FeedbackActions: FunctionComponent<{ readonly closeActionsSelector: () => void }> = ({
    closeActionsSelector,
}) => {
    const requestNewSkillLabel = useFeedbackDialogStore((state) => state.requestNewSkillLabel);
    const submitFeedbackLabel = useFeedbackDialogStore((state) => state.submitFeedbackLabel);
    const reportSkillLabel = useFeedbackDialogStore((state) => state.reportSkillLabel);
    const reportContentIssueLabel = useFeedbackDialogStore((state) => state.reportContentIssueLabel);
    const { openFeedbackModal } = useSkillFeedbackModalContext();
    const setFeedbackType = useFeedbackDialogStore((state) => state.setFeedbackType);
    const sendFeedbackToCobrainer = useFeedbackDialogStore((state) => state.sendFeedbackToCobrainer);
    const feedbackDescriptionLabel = useFeedbackDialogStore((state) => state.feedbackDescriptionLabel);

    const sendFeedback = (): void => {
        sendFeedbackToCobrainer(feedbackDescriptionLabel);
        closeActionsSelector();
    };

    const onClickRequest = (): void => {
        setFeedbackType('request');
        openFeedbackModal();
        closeActionsSelector();
    };

    const onClickReport = (): void => {
        setFeedbackType('report');
        openFeedbackModal();
        closeActionsSelector();
    };

    const onClickChange = (): void => {
        setFeedbackType('change');
        openFeedbackModal();
        closeActionsSelector();
    };

    return (
        <>
            <DropdownMenuItem onSelect={sendFeedback}>{submitFeedbackLabel}</DropdownMenuItem>
            <DropdownMenuItem onSelect={onClickRequest}>{requestNewSkillLabel}</DropdownMenuItem>
            <DropdownMenuItem onSelect={onClickReport}>{reportSkillLabel}</DropdownMenuItem>
            <DropdownMenuItem onSelect={onClickChange}>{reportContentIssueLabel}</DropdownMenuItem>
        </>
    );
};

export const SkillFeedbackButton: FunctionComponent = () => {
    const { isExpanded, setOverlayOpen, shouldContentOverflow } = useNavigationMenu();
    const translate = useTranslate();
    const clearFeedback = useFeedbackDialogStore((state) => state.clearFeedback);

    const [open, setOpen] = useState(false);
    const shouldShowExpandedLayout = isExpanded || shouldContentOverflow;

    useEffect(() => {
        return () => {
            setOverlayOpen(false);
        };
    }, [setOverlayOpen]);

    const handleOpenChange = (nextOpen: boolean): void => {
        setOpen(nextOpen);
        setOverlayOpen(nextOpen);

        if (!nextOpen) {
            clearFeedback();
        }
    };

    const closeMenu = (): void => {
        setOpen(false);
        setOverlayOpen(false);
        clearFeedback();
    };

    return (
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger>
                <Button
                    aria-label={translate('common.help')}
                    className="w-full justify-start overflow-hidden p-l! text-left focus-visible:outline focus-visible:outline-1 focus-visible:outline-orange-800"
                    iconOnly={!shouldShowExpandedLayout}
                    iconPosition="leading"
                    isActive={open}
                    variant="tertiary"
                >
                    <div className="flex items-center text-gray-600">
                        <IconLifeBuoy size="1rem" />
                    </div>
                    <span
                        className={`flex flex-1 min-w-0 whitespace-nowrap text-sm font-medium leading-none text-gray-800 transition-opacity duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
                    >
                        {translate('common.help')}
                    </span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" sideOffset={-24}>
                <FeedbackActions closeActionsSelector={closeMenu} />
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
